import * as React from 'react'
import { Outlet } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Container from '@mui/material/Container'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import GlobalStyles from '@mui/material/GlobalStyles'
import * as yup from 'yup'
import { PrivateRoute } from '@clara/auth'
import { GeneralError } from '@/components/general-error'
import { MaybeRenderProp, WithChildren, lazyLoad } from '@clara/react-utils'
import {
  TopNavWider,
  Header,
  HeaderElement,
  TopNavElement,
  SidePanelProvider,
} from '@clara/layout-next'
import { useT } from '@clara/locale'
import { useEffectOnce } from '@clara/hooks'
import { useNotifications } from '@clara/notifications'

import {
  GlobalStateProvider,
  useGlobalStateContext,
} from '@/store/global/use-global-state'
import { locale } from '@/utils/yup'
import { LayoutButtons } from '@/features/reimbursements/components/layout-buttons'
import { LayoutTabs } from '@/features/reimbursements/components/layout-tabs'
import { FeatureFlagRouteGuard } from './guards'
import { ReimbursementDetailProvider } from '@/features/reimbursements/state/detail/detail'

import { ManageReimbursement } from '@/features/manage-reimbursement/components/manage-reimbursement'
import { ManageReimbursementStore } from '@/features/manage-reimbursement/stores/manage-reimbursement-store'
import { ManageReimbursementSuccess } from '@/features/manage-reimbursement/components/manage-reimbursment-success'
import GettingStartedPage from '@/features/reimbursements/components/getting-started'
import { BankInfoBlankState } from '@/features/reimbursements/components/bank-info'
import { ManageReimbursementProcessing } from '@/features/manage-reimbursement/components/manage-reimbursment-processing'
import { ManageReimbursementError } from '@/features/manage-reimbursement/components/manage-reimbursment-error'
import { env } from '@/config'
import { useBackNavigation } from '@/hooks/use-back-navigation'
import { get, keyBy, runIfFn } from '@clara/utils'
import { useEvents } from '@/hooks/use-analytics'
import UserOperationStatus from '@/utils/user-operation-status'
import { useUserOperationStatus } from '@/hooks/use-user-operation-status'
import { OPERATION_COUNTRY } from '@/config/env'

const ReimbursementsPage = lazyLoad(
  () => import('../features/reimbursements'),
  (m) => m.default,
)

const PendingReimbursementsPage = lazyLoad(
  () => import('../features/reimbursements'),
  (m) => m.PendingReimbursementsPage,
)

const ToPayReimbursementsPage = lazyLoad(
  () =>
    import(
      '../features/reimbursements/components/table-and-filters/to-pay/table'
    ),
  (m) => m.ToPayReimbursementsPage,
)

const ReimbursementDetailPage = lazyLoad(
  () => import('../features/reimbursements'),
  (m) => m.ReimbursementDetailPage,
)

yup.setLocale(locale)

type HighlightRenderProps = {
  isLoading: boolean
  isHighlighted: boolean
  highlightLabel: string
  highlightColor: 'info' | 'pro'
}

type HighlightProps = {
  children?: MaybeRenderProp<HighlightRenderProps>
}

const Highlight: React.FC<HighlightProps> = ({ children: childrenProp }) => {
  const { state } = useNotifications()

  const props: HighlightRenderProps = React.useMemo(() => {
    const notifications = keyBy(
      state.value ?? [],
      (nottification) => nottification.key,
    )
    const match = get(notifications, '/reimbursements')

    // TODO: disabled temporarily
    // const isHighlighted = isNotEmptyObject(match)
    const isHighlighted = OPERATION_COUNTRY !== 'BR'
    const isExperiment = match?.status === 'experiments'
    return {
      isLoading: state.loading,
      isHighlighted,
      highlightLabel: isExperiment ? 'beta' : 'pro',
      highlightColor: isExperiment ? 'info' : 'pro',
    }
  }, [state])

  const children = runIfFn(childrenProp, props)

  return <React.Fragment>{children}</React.Fragment>
}

const DashboardLayout = (props: WithChildren) => {
  const { t } = useT(['reimbursements', 'common'])
  const {
    getBankInfo,
    bankInfo,
    bankInfoIsLoading,
    getUserInfo,
    isEmployee,
    userInfoResponse,
    isManagerOrCompanyOwner,
  } = useGlobalStateContext()

  const {
    getUserOperationStatusResponse,
    postUserOperationStatus,
    postUserOperationStatusResponse,
    userClickedStartToUseButton,
  } = useUserOperationStatus(UserOperationStatus.StartToUse)

  const events = useEvents()

  useEffectOnce(() => {
    events.onHomepage()

    getBankInfo()
    getUserInfo()
  })

  if (bankInfoIsLoading || getUserOperationStatusResponse.loading) return null

  if (userInfoResponse.error) return <GeneralError />

  if (!userClickedStartToUseButton)
    return (
      <GettingStartedPage
        onStartToUseClick={postUserOperationStatus}
        isLoading={postUserOperationStatusResponse.loading}
      />
    )

  if (isEmployee && !isManagerOrCompanyOwner && !bankInfo)
    return <BankInfoBlankState />

  const title = <span>{t('main.title')}</span>

  return (
    <React.Fragment>
      <Helmet>
        <title>{t('main.title')}</title>
      </Helmet>
      <Header>
        <HeaderElement variant="title" sx={{ mt: -1 }}>
          <Highlight>
            {(props) => (
              <Stack spacing={1} direction="row" alignItems="center">
                {title}
                {props.isHighlighted && (
                  <Chip
                    size="small"
                    color={'pro'}
                    label={t('pro', { ns: 'common' })}
                  />
                )}
              </Stack>
            )}
          </Highlight>
        </HeaderElement>
        <HeaderElement variant="actions">
          <LayoutButtons />
        </HeaderElement>
        <HeaderElement variant="navigation" sx={{ gap: 2 }}>
          <LayoutTabs />
        </HeaderElement>
      </Header>
      <Container maxWidth={false}>
        {props.children}
        <Outlet></Outlet>
      </Container>
    </React.Fragment>
  )
}

const FullscreenLayout = (props: WithChildren) => {
  const { t } = useT('reimbursements')
  const headerTitle = props.headerTitle
    ? t(`${props.headerTitle}`)
    : t('main.actions.add.request')

  const navigate = useBackNavigation()

  return (
    <GlobalStateProvider>
      <SidePanelProvider>
        <React.Fragment>
          <GlobalStyles
            styles={(theme) => ({
              body: {
                backgroundColor: theme.palette.v3.bg.alternative,
              },
            })}
          ></GlobalStyles>
          <TopNavWider
            zendeskHost={env.ZENDESK_HOST}
            onClose={() => navigate()}
          >
            <TopNavElement variant="title">{headerTitle}</TopNavElement>
          </TopNavWider>
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              height: 'calc(var(--ui-vh) - 56px)',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
            }}
          >
            {props.children}
            <Outlet></Outlet>
          </Container>
        </React.Fragment>
      </SidePanelProvider>
    </GlobalStateProvider>
  )
}

const routes = [
  {
    layout: 'dashboard',
    path: '/reimbursements',
    element: (
      <GlobalStateProvider>
        <PrivateRoute>
          <FeatureFlagRouteGuard>
            <DashboardLayout />
          </FeatureFlagRouteGuard>
        </PrivateRoute>
      </GlobalStateProvider>
    ),
    children: [
      {
        index: true,
        element: <ReimbursementsPage />,
      },
      {
        path: '/reimbursements/pending',
        element: <PendingReimbursementsPage />,
      },
      {
        path: '/reimbursements/pay',
        element: <ToPayReimbursementsPage />,
      },
    ],
  },
  {
    layout: 'fullscreen',
    path: '/reimbursements/create',
    element: (
      <GlobalStateProvider>
        <PrivateRoute>
          <FeatureFlagRouteGuard>
            <ManageReimbursementStore>
              <FullscreenLayout />
            </ManageReimbursementStore>
          </FeatureFlagRouteGuard>
        </PrivateRoute>
      </GlobalStateProvider>
    ),
    children: [
      {
        index: true,
        element: <ManageReimbursement />,
      },
      {
        path: '/reimbursements/create/processing',
        element: <ManageReimbursementProcessing />,
      },
      {
        path: '/reimbursements/create/success',
        element: <ManageReimbursementSuccess />,
      },
      {
        path: '/reimbursements/create/error',
        element: <ManageReimbursementError />,
      },
    ],
  },
  {
    layout: 'fullscreen',
    path: '/reimbursements/update/:uid',
    element: (
      <GlobalStateProvider>
        <PrivateRoute>
          <FeatureFlagRouteGuard>
            <ManageReimbursementStore>
              <FullscreenLayout />
            </ManageReimbursementStore>
          </FeatureFlagRouteGuard>
        </PrivateRoute>
      </GlobalStateProvider>
    ),
    children: [
      {
        index: true,
        element: <ManageReimbursement />,
      },
      {
        path: '/reimbursements/update/:uid/processing',
        element: <ManageReimbursementProcessing />,
      },
      {
        path: '/reimbursements/update/:uid/success',
        element: <ManageReimbursementSuccess />,
      },
      {
        path: '/reimbursements/update/:uid/error',
        element: <ManageReimbursementError />,
      },
    ],
  },
  {
    layout: 'fullscreen',
    path: '/reimbursements/:id',
    element: (
      <GlobalStateProvider>
        <PrivateRoute>
          <ReimbursementDetailProvider>
            <FullscreenLayout headerTitle={'detail.main.title'} />
          </ReimbursementDetailProvider>
        </PrivateRoute>
      </GlobalStateProvider>
    ),
    children: [
      {
        index: true,
        element: <ReimbursementDetailPage />,
      },
    ],
  },
  {
    layout: 'dashboard',
    path: '/reimbursements/error',
    element: (
      <GlobalStateProvider>
        <PrivateRoute>
          <DashboardLayout />
        </PrivateRoute>
      </GlobalStateProvider>
    ),
    children: [
      {
        index: true,
        element: <GeneralError />,
      },
    ],
  },
]

export default routes
